import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowUpRight, Clock3, Star, UtensilsCrossed } from "lucide-react";
import type { ReactNode } from "react";

import { listPublicDistricts } from "@/lib/districts.functions";
import { SmartImage } from "@/components/ui/SmartImage";
import { DistrictLink } from "@/components/districts/DistrictLink";
import { CalendarIcon } from "@/components/icons/NavIcons";
import { HouseBuildingIcon, LandLayerIcon } from "@/components/icons/LaneIcons";
import { MapMarkerPinIcon } from "@/components/icons/MapMarkerPinIcon";
import { FinlandFlagIcon } from "@/components/icons/FinlandFlagIcon";
import { usePublicCities } from "@/hooks/use-public-cities";

export const districtsQuery = queryOptions({
  queryKey: ["public-districts"],
  queryFn: () => listPublicDistricts(),
});

type PublicDistrict = Awaited<ReturnType<typeof listPublicDistricts>>[number];

export const Route = createFileRoute("/districts/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(districtsQuery),
  head: ({ loaderData }) => {
    const districts = (loaderData ?? []) as PublicDistrict[];
    const title = "Official Districts & Partners in Finland | what";
    const desc =
      "Explore Finland's official district hubs — see every restaurant and bar in the area, which ones are open right now and what's on the menu today.";
    const url = "https://whatfinland.com/districts";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Official Districts & Partners",
            url,
            numberOfItems: districts.length,
            itemListElement: districts.map((d, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: d.name,
              url:
                d.slug === "train-factory" || d.slug === "konepaja"
                  ? "https://whatfinland.com/train-factory"
                  : `https://whatfinland.com/districts/${d.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: DistrictsIndex,
});

function isTrainFactory(slug: string) {
  return slug === "train-factory" || slug === "konepaja";
}

function districtHero(d: PublicDistrict): string | null {
  return d.hero_image_url;
}

function prettyCity(slug: string | null, cities: { slug: string; name: string }[]) {
  if (!slug) return null;
  return cities.find((c) => c.slug === slug)?.name ?? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DistrictsIndex() {
  const { data: districts } = useSuspenseQuery(districtsQuery);
  const cities = usePublicCities();

  const partners = districts.filter((d) => d.official_partner);
  const cityCount = new Set(districts.map((d) => d.city_slug).filter(Boolean)).size;
  const featured = partners[0] ?? districts[0] ?? null;
  const rest = featured ? districts.filter((d) => d.slug !== featured.slug) : [];

  return (
    <div className="mx-auto max-w-7xl px-4 pb-16 pt-6 sm:px-6 md:pb-20 md:pt-10">
      <nav aria-label="Breadcrumb" className="mb-5">
        <ol className="flex items-center gap-2 text-[11px] font-medium text-navy/45" style={{ textTransform: "none" }}>
          <li>
            <Link to="/" className="hover:text-navy">
              Home
            </Link>
          </li>
          <li aria-hidden className="text-navy/25">
            /
          </li>
          <li className="text-navy">Districts</li>
        </ol>
      </nav>

      <header className="mb-8 md:mb-10">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
          <div className="flex min-w-0 items-center gap-3.5 sm:gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-navy text-white shadow-[0_12px_28px_-12px_oklch(0.22_0.09_260/0.75)] sm:size-[3.75rem]">
              <LandLayerIcon className="size-6 sm:size-7" />
            </span>
            <div className="min-w-0">
              <p className="font-body text-[10px] font-semibold uppercase tracking-[0.22em] text-navy/45 sm:text-[11px]">
                Official partners
              </p>
              <h1 className="mt-0.5 font-display text-[2.5rem] font-extrabold leading-[0.84] tracking-tighter text-navy lowercase sm:text-[3.5rem] md:text-[4.25rem]">
                districts
              </h1>
            </div>
          </div>
          <p
            style={{ textTransform: "none" }}
            className="max-w-md font-body text-[13px] leading-relaxed text-navy/60 sm:text-[15px] lg:pb-1 lg:text-right"
          >
            Neighbourhood hubs with every venue in one place — hours, menus and events, kept current by the venues
            themselves.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 overflow-hidden rounded-2xl bg-white shadow-[0_10px_32px_-20px_oklch(0.22_0.09_260/0.35)] ring-1 ring-border sm:mt-7 sm:rounded-3xl lg:grid-cols-4">
          <SummaryTile
            icon={<LandLayerIcon className="size-4" />}
            value={String(districts.length)}
            label={districts.length === 1 ? "District" : "Districts"}
          />
          <SummaryTile
            icon={<Star className="size-4 fill-current" />}
            value={String(partners.length)}
            label={partners.length === 1 ? "Partner" : "Partners"}
          />
          <SummaryTile
            icon={<HouseBuildingIcon className="size-4" />}
            value={String(cityCount || "—")}
            label={cityCount === 1 ? "City" : "Cities"}
          />
          <SummaryTile icon={<FinlandFlagIcon className="size-5" />} value="Finland" label="Live hubs" flag />
        </div>
      </header>

      {districts.length === 0 ? (
        <p className="rounded-[1.5rem] border border-dashed border-navy/15 bg-white p-10 text-center text-sm text-navy/60">
          No districts published yet.
        </p>
      ) : (
        <div className="space-y-8 md:space-y-10">
          {featured ? (
            <FeaturedDistrict
              district={featured}
              city={prettyCity(featured.city_slug, cities)}
              solo={rest.length === 0}
            />
          ) : null}

          {rest.length > 0 ? (
            <section>
              <div className="mb-4 flex items-end justify-between gap-3 sm:mb-5">
                <h2 className="font-heading text-[22px] leading-none tracking-tight text-navy sm:text-[28px]">
                  All hubs
                </h2>
                <p className="text-[12px] font-medium text-navy/45">{rest.length + (featured ? 1 : 0)} districts</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                {rest.map((d) => (
                  <DistrictCard key={d.slug} district={d} city={prettyCity(d.city_slug, cities)} />
                ))}
              </div>
            </section>
          ) : null}

          <section className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3">
            <InfoTile
              icon={<Clock3 className="size-4" />}
              title="Hours"
              body="See which kitchens are open right now, straight from each venue."
            />
            <InfoTile
              icon={<UtensilsCrossed className="size-4" />}
              title="Menus"
              body="Lunch lists and menus stay in one hub, updated by the restaurants."
            />
            <InfoTile
              icon={<CalendarIcon className="size-5" />}
              title="Events"
              body="What’s on this week in the neighbourhood, without hunting around."
            />
          </section>
        </div>
      )}
    </div>
  );
}

function FeaturedDistrict({
  district,
  city,
  solo,
}: {
  district: PublicDistrict;
  city: string | null;
  solo: boolean;
}) {
  const hero = districtHero(district);
  return (
    <DistrictLink
      slug={district.slug}
      className={`group relative block overflow-hidden rounded-[1.6rem] text-white shadow-[0_22px_50px_-28px_oklch(0.22_0.09_260/0.55)] ring-1 ring-black/10 sm:rounded-[1.85rem] ${
        solo ? "min-h-[300px] sm:min-h-[400px] lg:min-h-[460px]" : "min-h-[260px] sm:min-h-[340px] lg:min-h-[400px]"
      }`}
    >
      {hero ? (
        <SmartImage
          src={hero}
          alt={district.name}
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 size-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.04] sm:object-[center_58%]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-royal to-navy" />
      )}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,16,32,0.28) 0%, rgba(8,16,32,0.08) 38%, rgba(8,16,32,0.78) 100%), linear-gradient(90deg, rgba(8,16,32,0.42) 0%, rgba(8,16,32,0.12) 46%, rgba(8,16,32,0) 78%)",
        }}
      />

      <div className="relative flex h-full min-h-[inherit] flex-col justify-between p-5 sm:p-7 lg:p-9">
        <div className="flex flex-wrap items-center gap-2">
          {district.official_partner ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/14 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white ring-1 ring-white/20 backdrop-blur-md">
              <Star className="size-3 fill-current" /> Official partner
            </span>
          ) : null}
          {city ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/12 px-2.5 py-1 text-[11px] font-semibold text-white/90 ring-1 ring-white/15 backdrop-blur-md">
              <MapMarkerPinIcon className="size-3" /> {city}
            </span>
          ) : null}
        </div>

        <div className="max-w-xl">
          <h2 className="font-heading text-[34px] leading-[0.86] tracking-tight uppercase sm:text-5xl lg:text-[56px] lg:leading-[0.86]">
            {isTrainFactory(district.slug) ? (
              <>
                Train Factory
                <span className="block">District</span>
              </>
            ) : (
              district.name
            )}
          </h2>
          {district.tagline ? (
            <p
              style={{ textTransform: "none" }}
              className="mt-2.5 line-clamp-2 max-w-[36ch] text-[13px] font-medium leading-snug text-white/88 sm:mt-3 sm:text-[16px]"
            >
              {district.tagline}
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            {district.venue_count > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1.5 text-[12px] font-semibold ring-1 ring-white/15 backdrop-blur-md">
                <UtensilsCrossed className="size-3.5" />
                <span className="tabular-nums">{district.venue_count}</span> places
              </span>
            ) : null}
            <span className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-4 text-[13px] font-semibold text-navy shadow-sm">
              Explore hub
              <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </div>
    </DistrictLink>
  );
}

function DistrictCard({ district, city }: { district: PublicDistrict; city: string | null }) {
  const hero = districtHero(district);
  return (
    <DistrictLink
      slug={district.slug}
      className="group relative block overflow-hidden rounded-[1.35rem] text-white ring-1 ring-black/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_44px_-22px_oklch(0.22_0.09_260/0.45)] sm:rounded-[1.5rem]"
    >
      <div className="relative aspect-[4/5] sm:aspect-[5/6]">
        {hero ? (
          <SmartImage
            src={hero}
            alt={district.name}
            className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-royal to-navy" />
        )}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, oklch(0.22 0.14 270 / 0.88) 0%, oklch(0.22 0.14 270 / 0.42) 42%, transparent 78%)",
          }}
        />
        {district.official_partner ? (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy">
            <Star className="size-2.5 fill-current" /> Partner
          </span>
        ) : null}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
          {city ? <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70">{city}</p> : null}
          <h3 className="mt-1 font-heading text-[1.35rem] leading-[0.95] tracking-tight">{district.name}</h3>
          {district.tagline ? (
            <p style={{ textTransform: "none" }} className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-white/85">
              {district.tagline}
            </p>
          ) : null}
          <div className="mt-3 flex items-center justify-between gap-2">
            {district.venue_count > 0 ? (
              <span className="text-[12px] font-semibold text-white/80">
                <span className="tabular-nums">{district.venue_count}</span> places
              </span>
            ) : (
              <span />
            )}
            <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold">
              Open
              <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </div>
    </DistrictLink>
  );
}

function SummaryTile({
  icon,
  value,
  label,
  flag = false,
}: {
  icon: ReactNode;
  value: string;
  label: string;
  flag?: boolean;
}) {
  return (
    <div className="flex min-h-[76px] items-center gap-3 border-border px-4 py-3.5 max-lg:odd:border-r max-lg:[&:nth-child(-n+2)]:border-b sm:px-5 sm:py-4 lg:border-r lg:last:border-r-0">
      <span
        className={`grid size-10 shrink-0 place-items-center rounded-full ${
          flag ? "bg-white ring-1 ring-border overflow-hidden" : "bg-navy text-white"
        }`}
      >
        {icon}
      </span>
      <div className="min-w-0 text-left">
        <div className="truncate font-heading text-lg leading-none tracking-tight text-navy sm:text-xl">{value}</div>
        <div className="mt-1 truncate font-mono text-[9px] uppercase tracking-[0.16em] text-navy/45">{label}</div>
      </div>
    </div>
  );
}

function InfoTile({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-border sm:p-5">
      <span className="grid size-9 place-items-center rounded-full bg-navy text-white">{icon}</span>
      <h3 className="mt-3 font-heading text-[16px] leading-none tracking-tight text-navy">{title}</h3>
      <p style={{ textTransform: "none" }} className="mt-1.5 text-[13px] leading-snug text-navy/60">
        {body}
      </p>
    </div>
  );
}
